import { Center, Loader } from "@mantine/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo } from "react";
import { Navigate, useLocation } from "react-router";
import { ApiError, AUTH_EXPIRED_EVENT, api } from "../api/client";
import type { AuthUser } from "../api/types";
import { applyProfileLocale } from "../i18n";

interface AuthState {
  user: AuthUser | null;
  loading: boolean;
  login(username: string, password: string): Promise<AuthUser>;
  logout(): Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const me = useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      try {
        const r = await api<{ user: AuthUser }>("/api/auth/me");
        return r.user;
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
    retry: false,
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    if (me.data) applyProfileLocale(me.data.locale);
  }, [me.data]);

  useEffect(() => {
    const onExpired = () => queryClient.setQueryData(["me"], null);
    window.addEventListener(AUTH_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired);
  }, [queryClient]);

  const login = useCallback(
    async (username: string, password: string) => {
      const r = await api<{ user: AuthUser }>("/api/auth/login", {
        method: "POST",
        body: { username, password },
      });
      // A session that expired only nulled ["me"]; a different user signing in on the same tab
      // must not inherit the previous user's cached lists (e.g. the admin-only bin).
      queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== "me" });
      queryClient.setQueryData(["me"], r.user);
      return r.user;
    },
    [queryClient],
  );

  const logout = useCallback(async () => {
    await api("/api/auth/logout", { method: "POST" }).catch(() => undefined);
    queryClient.setQueryData(["me"], null);
    queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== "me" });
  }, [queryClient]);

  const value = useMemo<AuthState>(
    () => ({ user: me.data ?? null, loading: me.isLoading, login, logout }),
    [me.data, me.isLoading, login, logout],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth outside AuthProvider");
  return ctx;
}

/** Redirects anonymous visitors to /login, remembering where they wanted to go. */
export function RequireAuth({ children, admin = false }: { children: ReactNode; admin?: boolean }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <Center h="100vh">
        <Loader />
      </Center>
    );
  }
  if (!user)
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  if (admin && user.role !== "admin") return <Navigate to="/" replace />;
  return <>{children}</>;
}
